import { useRef, useState } from "react";
import { Chart } from "chart.js/auto";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DistributionData, RawIndicatorData } from "../../services/types";
import { capitalizeWords, formatValue } from "../../utils/helpers";
import { noDataPlugin } from "../../utils/chartUtils";

interface DistributionChartProps {
    distributionData: RawIndicatorData[] | null;
    countyDistributionData: DistributionData[] | null;
    id: number;
    metricName: string;
    lod: string;
}

function DistributionChart({ distributionData, countyDistributionData, id, metricName, lod }: DistributionChartProps) {
    // Register the plugins to all charts:
    Chart.register(ChartDataLabels);
    // Unregister the noDataPlugin explicitly
    Chart.unregister(noDataPlugin); 
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    // NEW: a11y text state
    const [a11yShort, setA11yShort] = useState<string>("");
    const [a11yLong, setA11yLong] = useState<string>("");

    // Stable id for aria-describedby (you already had this)
    const a11yId = `chart-desc-${id || metricName.replace(/\s+/g, "-").toLowerCase()}`;

    useDeepCompareEffect(() => {
        if (!countyDistributionData) return;

        // Find the data object for the given ID
        const distribution = distributionData ? distributionData[0] : null;
        const countyDistribution = countyDistributionData[0];

        console.log('distribution data:', distribution);
        if (!countyDistribution.versions?.[0]?.buckets) return;

        // Extract the bucket data
        const buckets = countyDistribution.versions[0].buckets;
        const dataEntries = distribution?.versions[0].data;
        // Extract the unit
        const unit = countyDistribution.versions[0].unit;

        console.log('buckets:', buckets);

        // Initialize an array to count occurrences in each bucket
        const bucketCounts = new Array(buckets.length).fill(0);

        // Iterate over dataEntries and count occurrences in the correct bucket
        dataEntries?.forEach(({ value }) => {
            const matchingBucketIndex = buckets.findIndex(
                (bucket) => value >= bucket.min_value && value <= bucket.max_value
            );
            
            if (matchingBucketIndex !== -1) {
                bucketCounts[matchingBucketIndex] += 1; // Increment count in the correct bucket
            }
        });

        // Extract county bucket counts
        const countyCounts = buckets.map((bucket) => bucket.count);

        // Calculate total counts for normalization
        const totalSelectedCount = bucketCounts.reduce((sum, count) => sum + count, 0);
        const totalCountyCount = countyCounts.reduce((sum, count) => sum + count, 0);

        console.log('totla county count:', totalCountyCount);

        // Normalize both datasets to percentages
        const normalizedData = totalSelectedCount > 0 
            ? bucketCounts.map(count => (count / totalSelectedCount) * 100)
            : bucketCounts;

        const normalizedCountyData = totalCountyCount > 0 
            ? countyCounts.map(count => (count / totalCountyCount) * 100)
            : countyCounts;

        console.log('normalized data:', normalizedData);

        // Prepare chart data
        const labels = buckets.map(
            (bucket) => `${formatValue(bucket.min_value, unit, id)} - ${formatValue(bucket.max_value, unit, id)}`
        );

        // ---------- NEW: build accessible texts ----------
        const hasAnyData =
        (normalizedData?.some(v => v > 0) || false) ||
        (normalizedCountyData?.some(v => v > 0) || false);

        // Short “alt” text used on aria-label
        const shortAlt = hasAnyData
        ? `${metricName} — distribution bar chart with ${labels.length} buckets, comparing Selected Area and King County.`
        : `${metricName}: no data available.`;

        // Long description (concise but complete)
        const pickTop = (arr: number[]) => {
        if (!arr?.length) return { idx: -1, val: 0 };
        let idx = 0; let val = arr[0];
        arr.forEach((v, i) => { if (v > val) { val = v; idx = i; } });
        return { idx, val };
        };

        const selTop = pickTop(normalizedData);
        const ctyTop = pickTop(normalizedCountyData);

        const listBuckets = labels.map((label, i) => {
        const sel = normalizedData?.[i] ?? 0;
        const cty = normalizedCountyData?.[i] ?? 0;
        // Use integers to keep output tidy
        const selPct = Number.isFinite(sel) ? `${sel.toFixed(0)}%` : "0%";
        const ctyPct = Number.isFinite(cty) ? `${cty.toFixed(0)}%` : "0%";
        return `${label}: Selected Area ${selPct}, King County ${ctyPct}`;
        }).join("; ");

        const longDesc = hasAnyData
        ? [
            `${metricName}. A distribution across ${labels.length} buckets, showing Selected Area vs King County.`,
            selTop.idx >= 0 ? `Selected Area peaks at "${labels[selTop.idx]}" (${selTop.val.toFixed(0)}%).` : ``,
            ctyTop.idx >= 0 ? `King County peaks at "${labels[ctyTop.idx]}" (${ctyTop.val.toFixed(0)}%).` : ``,
            `Bucket details — ${listBuckets}.`
            ].filter(Boolean).join(" ")
        : `${metricName}: no data to describe.`;

        // Store texts for ARIA
        setA11yShort(shortAlt);
        setA11yLong(longDesc);

        // Chart.js configuration
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {

            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: "Selected Area",
                            data: normalizedData,
                            backgroundColor: '#21506f',
                            borderRadius: 3,
                            hidden: normalizedData.every(value => value === 0),
                        },
                        {
                            label: "King County",
                            data: normalizedCountyData,
                            backgroundColor: '#88B637',
                            borderRadius: 3,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            display: true,
                            labels: {
                                filter: (legendItem, chartData) => {
                                    const datasetIndex = legendItem.datasetIndex;
                                    if (datasetIndex !== undefined && chartData.datasets[datasetIndex]) {
                                    const dataset = chartData.datasets[datasetIndex];
                                    return !dataset.hidden;
                                    }
                                    return false; // Hide the legend if datasetIndex is undefined
                                },
                                font: {
                                    size: 10,
                                },
                                boxWidth: 10
                            }
                        },
                        title: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                // Ensure tooltip header (title) is one line and remove comma
                                title: (tooltipItems) => {
                                    let title = tooltipItems[0].label || '';
                                    return title; // Replace commas with spaces
                                },
                                label: (tooltipItem) => {
                                    const datasetLabel = tooltipItem.dataset.label || 'Data';
                                    const percentage = `${Number(tooltipItem.raw).toFixed(2)}%`;
                                    const total =
                                    datasetLabel.toLowerCase().includes("county") ? totalCountyCount : totalSelectedCount;

                                    const count = `${Math.round(Number(tooltipItem.raw) / 100 * total)}`;

                                    return [
                                        `${datasetLabel}`,
                                        `Share of ${capitalizeWords(lod)}s: ${percentage}`, 
                                        `Count of ${capitalizeWords(lod)}s: ${count}`
                                    ];
                                },
                            },
                            backgroundColor: "#FFFFFF",
                            titleColor: "#4D4D4D", 
                            bodyColor: "#OB93CE",
                            borderColor: "#4D4D4D",
                            borderWidth: 1,
                            titleFont: {
                                family: 'Gotham'
                            },
                            bodyFont: {
                                family: 'Gotham'
                            }
                        },
                        datalabels: {
                            color: '#4D4D4D',
                            anchor: 'end',
                            align: 'start',
                            formatter:(value) => value ? `${value.toFixed(0)}%` : null,
                            font: {
                                family: 'Gotham',
                                size: 10,
                                weight: 'normal'
                            },
                            offset: -15,
                        },
                    },
                    layout: {
                        padding: {
                            left: 10,
                            right: 10,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false,
                                drawTicks: true,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                color: '#4D4D4D',
                                autoSkip: false,
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    family: 'Gotham',
                                    size: 8,
                                    weight: 'normal'
                                },
                                callback: function (value) {
                                    // Wrap labels by splitting into multiple lines
                                    const label = this.getLabelForValue(Number(value));
                                    const maxLength = 10; // Max length per line
                                    const words = label.split(/\s+/);
                                    let wrappedLabel = [];
                                    let currentLine = "";
                    
                                    for (let word of words) {
                                        if ((currentLine + word).length > maxLength) {
                                            wrappedLabel.push(currentLine);
                                            currentLine = word;
                                        } else {
                                            currentLine += (currentLine ? " " : "") + word;
                                        }
                                    }
                                    if (currentLine) wrappedLabel.push(currentLine);
                    
                                    return wrappedLabel; // Chart.js will render each array element as a new line
                                },
                            },
                        },
                        y: {
                            beginAtZero: true,
                            max: 100,
                            title: {
                                display: true,
                                text: `Share of ${capitalizeWords(lod)}s`,
                                font: {
                                    family: 'Gotham',
                                    size: 10,
                                    weight: 'bold',
                                },
                                color: '#4D4D4D',
                            },
                            ticks: {
                                color: '#4D4D4D',
                                autoSkip: false,
                                precision: 0,
                                callback: function(value) {
                                    return `${value}%`;
                                },
                                font: {
                                    family: 'Gotham',
                                    size: 10,
                                    weight: 'normal'
                                },
                                padding: 10
                            },
                            position: 'left',
                            grid: {
                                tickBorderDash: [2, 4]
                            },
                            border: {
                                display: false,
                                dash: [2,4]
                            },
                        },
                    },
                },
            });
        }
    }, [distributionData, countyDistributionData, id]);

    return ( 
        <figure className="chart-container w-full border border-solid border-lightGray rounded-md appearance-none bg-at-white">
            <canvas
                ref={chartRef}
                className=""
                role="img"
                aria-label={a11yShort}
                aria-describedby={a11yId}
            />
            {/* NEW: long description for screen readers only */}
            <div id={a11yId} className="sr-only">
                {a11yLong}
            </div>
        </figure>
     );
}

export default DistributionChart;